import { PLAYER_HISTORY } from "../data/playerHistory";

// Realism-guard thresholds — sanity bands for "how many players actually hit
// this counting-stat level" per position, used to catch a default-seed
// pipeline that's producing an implausible number of elite/replacement lines.
export const THRESHOLDS = {
  QB: [{ stat: "passYds", min: 5000 }, { stat: "passYds", min: 4500 }, { stat: "passYds", min: 4000 }, { stat: "passTD", min: 30 }],
  RB: [{ stat: "rushYds", min: 1500 }, { stat: "rushYds", min: 1200 }, { stat: "rushYds", min: 1000, flagLow: true }, { stat: "recYds", min: 500 }],
  WR: [{ stat: "recYds", min: 1500 }, { stat: "recYds", min: 1200 }, { stat: "recYds", min: 1000, flagLow: true }, { stat: "rec", min: 100 }],
  TE: [{ stat: "recYds", min: 1000 }, { stat: "recYds", min: 800 }],
};

export const HIST_YEARS = [2021, 2022, 2023, 2024, 2025];

let _histCountsCache = null;

// Module-memoized: one pass over PLAYER_HISTORY, tallying per-year counts of
// entries (by entry.pos) clearing each threshold, then reducing each
// (pos, stat, min) triple to {avg, minCt, maxCt} across 2021-2025.
export function computeHistThresholdCounts() {
  if (_histCountsCache) return _histCountsCache;

  const perYear = {}; // year -> "pos|stat|min" -> count
  // Pace (per-17-games) counts - only meaningful for flagLow thresholds, where
  // a shortened real season can hide an undershoot that a full-season pace
  // would actually clear.
  const pacePerYear = {}; // year -> "pos|stat|min" -> paced count
  HIST_YEARS.forEach(y => { perYear[y] = {}; pacePerYear[y] = {}; });

  for (const rows of Object.values(PLAYER_HISTORY)) {
    for (const entry of rows) {
      if (!HIST_YEARS.includes(entry.year)) continue;
      const posThresholds = THRESHOLDS[entry.pos];
      if (!posThresholds) continue;
      for (const t of posThresholds) {
        const v = entry[t.stat];
        const key = `${entry.pos}|${t.stat}|${t.min}`;
        if (v != null && v >= t.min) {
          perYear[entry.year][key] = (perYear[entry.year][key] || 0) + 1;
        }
        if (t.flagLow && entry.gms != null && entry.gms >= 8 && v != null && (v / entry.gms * 17) >= t.min) {
          pacePerYear[entry.year][key] = (pacePerYear[entry.year][key] || 0) + 1;
        }
      }
    }
  }

  const result = [];
  for (const [pos, thresholds] of Object.entries(THRESHOLDS)) {
    for (const t of thresholds) {
      const key = `${pos}|${t.stat}|${t.min}`;
      const counts = HIST_YEARS.map(y => perYear[y][key] || 0);
      const avg = counts.reduce((a, b) => a + b, 0) / counts.length;
      const entryResult = {
        pos, stat: t.stat, min: t.min, flagLow: !!t.flagLow,
        avg, minCt: Math.min(...counts), maxCt: Math.max(...counts),
        perYear: HIST_YEARS.map((y, i) => ({ year: y, count: counts[i] })),
      };
      if (t.flagLow) {
        const paceCounts = HIST_YEARS.map(y => pacePerYear[y][key] || 0);
        entryResult.paceAvg = paceCounts.reduce((a, b) => a + b, 0) / paceCounts.length;
        entryResult.paceMin = Math.min(...paceCounts);
        entryResult.paceMax = Math.max(...paceCounts);
        entryResult.pacePerYear = HIST_YEARS.map((y, i) => ({ year: y, count: paceCounts[i] }));
      }
      result.push(entryResult);
    }
  }

  _histCountsCache = result;
  return result;
}

// Counts how many projected `players` (from projectAllDefaults, each with a
// .stats sub-object) clear each threshold - the runtime counterpart to
// computeHistThresholdCounts, for comparing a live projection field to the
// historical band.
export function computeProjectedCounts(players) {
  const result = [];
  for (const [pos, thresholds] of Object.entries(THRESHOLDS)) {
    for (const t of thresholds) {
      const count = players.filter(p => p.pos === pos && p.stats?.[t.stat] != null && p.stats[t.stat] >= t.min).length;
      result.push({ pos, stat: t.stat, min: t.min, flagLow: !!t.flagLow, count });
    }
  }
  return result;
}

// Pace (per-17-games) counterpart to computeProjectedCounts - flagLow
// thresholds only, comparing against computeHistThresholdCounts' paceAvg/
// paceMin/paceMax.
export function computeProjectedPaceCounts(players) {
  const result = [];
  for (const [pos, thresholds] of Object.entries(THRESHOLDS)) {
    for (const t of thresholds) {
      if (!t.flagLow) continue;
      const count = players.filter(p =>
        p.pos === pos && p.games >= 8 && p.stats?.[t.stat] != null && (p.stats[t.stat] * 17 / p.games) >= t.min
      ).length;
      result.push({ pos, stat: t.stat, min: t.min, count });
    }
  }
  return result;
}

// Position-group envelope guards — 2018-25 5th-95th percentile bands for a
// skill group's share of the team's total allocated target/rush budget
// (source: vault Position_Group_Target_Split.md). Distinct from THRESHOLDS
// above (individual counting stats) - this is a structural allocation
// sanity check, read directly off an in-progress ALLOC-step allocs object.
export const GROUP_ENVELOPES = {
  targets: {
    WR: [45.8, 71.0],
    TE: [11.8, 32.6],
    RB: [12.6, 27.9],
  },
  carries: {
    RB: [67.4, 91.1],
    QB: [6.8, 29.7], // QB carries = QB1 rushPct (the only QB-prefixed slot)
    WR: [0.6, 8.5],
  },
};

// ── Stat-line realism guards (chunk 4) ─────────────────────────────────────
// The guards above read an in-progress ALLOC object (shares). These read the
// team's finished STAT LINES, so they catch a different class of mistake: a
// team that allocates plausibly but, once efficiency rates are applied, ends up
// catching 80% of an implausible number of targets.
//
// ADVISORY ONLY. Nothing here blocks a save. A user is allowed to project an
// outlier — they just get told it is one.
//
// 🔴 BASIS. Every threshold below is a FULL-TEAM reference: all receiving lines
// for one team, depth included. That matches what the editor hands over
// (projectTeamPlayers returns RB3+/WR4+ alongside the starters), so it is right
// for the user-edited teams these fire on. It is NOT right for a set of
// starters-only lines — see computeStatLineFlags' `chartedOnly` note.
// All reference figures are 2025 actuals recomputed from PLAYER_HISTORY; the
// checks in the test file re-derive them rather than trusting these constants.

// Team receiving volume: total targets a team distributes in a season. FULL-TEAM
// basis — every receiving line including the depth/remainder rows.
export const TEAM_TARGET_ENVELOPE = [460, 580];

// CHARTED basis: the same quantity when only the NAMED starters are present
// (3 WR + 2 TE + 2 RB) and the remainder rows are absent by construction. A
// full-team envelope cannot grade that sum — it reads low no matter how good the
// projection is — so partial inputs get their own band.
//
// DERIVED, not chosen. Per team-season 2021-2025 in PLAYER_HISTORY, sum the
// targets of the top-7 receiving rows (top 3 WR, 2 TE, 2 RB by targets). 108
// team-seasons carry a full top-7 in that file; their distribution is
// p5 351 / median 441 / p95 543. The high end is used as-is.
//
// The LOW end is widened by an AVAILABILITY FACTOR, because a charted sum is
// deflated twice over relative to a realized one: the house rows carry modelled
// role-mean games rather than a full season, AND nothing absorbs the games they
// miss (the depth rows that would have caught those targets are the very rows
// that were dropped). Factor = house mean receiving games / 17 = 14.625 / 17 =
// 0.8603, so the floor is 351 x 0.8603 = 301.96, which rounds to the 302 below.
//
// 🔴 303 -> 302 AT 1fa06cb (FP Aug 27 refresh), and this is the first refresh
// that actually moved the constant. Mean receiving games fell 14.6607 -> 14.625,
// exactly 8 games over 224 receiving rows, all of it on corrected rows: Jeanty
// 15 -> 13, Kamara 14 -> 11, Tracy 15 -> 13, Love 15 -> 14, Pacheco 15 -> 14,
// Pierce 14 -> 13, and Tank Dell's 12-game row replaced by Jaylin Noel at 14.
// (It read 14.6964 / 0.8645 / 303.4 before the aug18 corrections cut NO's Jordyn
// Tyson to a 6-game line; that one did NOT move the constant.) The shipped set's
// smallest charted total is 326, so the move is inert on every team today — it
// keeps the constant equal to its own derivation, which is the only reason the
// number is defensible at all.
//
// 🔴 The factor is deliberately the CONSERVATIVE one and the alternative is
// recorded because the choice changes results. Measured house availability
// (14.625 games) is actually slightly HIGHER than the real top-7 rows' own
// average (14.28), so a house-vs-real ratio would be 1.0243 — no widening at all
// — and would flag 3 teams (MIA 326, BUF 345, CAR 345). It flagged 4 before the
// Aug 27 refresh; WAS left that set by going 357 -> 365 targets when
// Croskey-Merritt won the job. The /17 basis is used because this band's job is
// to avoid crying wolf on a legitimate partial input while still catching a
// structurally broken one; a false warning on a good team costs more here than a
// missed marginal low. The test re-derives both.
export const CHARTED_TARGET_ENVELOPE = [302, 543];

// Team-level receiver catch rate = rec / targets. This is NOT completion
// percentage — the denominator is targets thrown at the receiver group, not
// team pass attempts, and the two differ by throwaways, spikes and sacks. 2025
// actual league-wide: 67.7%, with team p5-p95 at 63.0-72.1%.
export const TEAM_CATCH_RATE_BAND = [62, 73];

// Per-player catch-rate p5-p95 among players clearing PLAYER_CATCH_MIN_TARGETS,
// 2025 actuals. RB is a genuinely different band (short, high-percentage
// targets), which is why this is positional and not one league-wide band.
export const PLAYER_CATCH_RATE_BANDS = {
  WR: [46.1, 75.8],
  TE: [60.8, 80.4],
  RB: [75.3, 85.9],
};

// Below this a catch rate is small-sample noise, not a projection choice.
export const PLAYER_CATCH_MIN_TARGETS = 70;

const RECEIVING_POS = new Set(["WR", "TE", "RB"]);

// A remainder/depth row: the aggregate slot that holds everything below the
// charted starters (RB3+, WR4+). projectTeamPlayers emits these for a live team;
// the generated house set drops them. Their presence is therefore the signal for
// which basis applies, and it is a structural fact about the input rather than
// something a caller has to remember to declare.
const REMAINDER_SLOT_RE = /^(QB|RB|WR|TE)\d*\+/;
export function isRemainderRow(line) {
  return REMAINDER_SLOT_RE.test(String(line?.slot ?? ""))
    || REMAINDER_SLOT_RE.test(String(line?.player ?? ""));
}

const statOf = (line, key) => {
  const v = line?.stats?.[key];
  return typeof v === "number" && Number.isFinite(v) ? v : 0;
};

/**
 * computeStatLineFlags(lines) -> [{ check, label, value, lo, hi, flag, detail }]
 *
 * `lines`: the team's projected player rows, shaped { player, pos, stats, games }
 * — i.e. exactly `projectTeamPlayers(...).players` or a save's
 * `effSummary.players`. Depth rows (RB3+/WR4+) SHOULD be included; they are part
 * of the team's real volume.
 *
 * Always returns one row per team-level check plus one per flagged player, so a
 * caller can render "checked and fine" as easily as a warning. `flag` is
 * "high" / "low" / null.
 *
 * BASIS IS SELECTED AT RUNTIME from the rows themselves: if any remainder row
 * (RB3+/WR4+) is present the input is a whole team and the volume check grades
 * against TEAM_TARGET_ENVELOPE; if none is, it is a named-starters subset and
 * grades against CHARTED_TARGET_ENVELOPE. Nothing to remember at the call site,
 * and a caller cannot silently get the wrong reference.
 *
 * 🔴 `chartedOnly`: retained for callers that want the team-level checks reported
 * but NOT graded at all (`flag: null`, `basis: "partial"`). It is no longer how
 * partial inputs are handled — runtime basis selection is — so passing it is now
 * an explicit "report, do not judge" request. The per-player check is unaffected
 * either way; it never aggregates.
 */
export function computeStatLineFlags(lines, { chartedOnly = false } = {}) {
  const rows = Array.isArray(lines) ? lines : [];
  const receiving = rows.filter((l) => RECEIVING_POS.has(l?.pos));

  const targets = receiving.reduce((s, l) => s + statOf(l, "targets"), 0);
  const rec = receiving.reduce((s, l) => s + statOf(l, "rec"), 0);

  const hasRemainder = rows.some(isRemainderRow);
  const out = [];

  const [tLo, tHi] = hasRemainder ? TEAM_TARGET_ENVELOPE : CHARTED_TARGET_ENVELOPE;
  out.push({
    check: "teamTargets",
    label: "team receiving volume",
    value: Math.round(targets),
    lo: tLo, hi: tHi,
    basis: chartedOnly ? "partial" : hasRemainder ? "full" : "charted",
    flag: chartedOnly || targets <= 0 ? null : targets > tHi ? "high" : targets < tLo ? "low" : null,
  });

  // Catch rate needs no basis split: it is a RATIO, so dropping the remainder
  // rows moves it only as far as the depth guys' rate differs from the starters'.
  // Measured, 2025: charted-7 67.89% vs all-rows 67.69% — 0.20pp. The volume sum
  // has no such property, which is why only that check is basis-dependent.
  const [cLo, cHi] = TEAM_CATCH_RATE_BAND;
  const catchPct = targets > 0 ? (rec / targets) * 100 : 0;
  out.push({
    check: "teamCatchRate",
    label: "team catch rate",
    value: Math.round(catchPct * 10) / 10,
    lo: cLo, hi: cHi,
    basis: chartedOnly ? "partial" : hasRemainder ? "full" : "charted",
    flag: chartedOnly || targets <= 0 ? null : catchPct > cHi ? "high" : catchPct < cLo ? "low" : null,
  });

  for (const l of receiving) {
    const band = PLAYER_CATCH_RATE_BANDS[l.pos];
    const tgt = statOf(l, "targets");
    if (!band || tgt < PLAYER_CATCH_MIN_TARGETS) continue;
    const pct = (statOf(l, "rec") / tgt) * 100;
    const flag = pct > band[1] ? "high" : pct < band[0] ? "low" : null;
    if (!flag) continue;
    out.push({
      check: "playerCatchRate",
      label: "catch rate",
      player: l.player,
      pos: l.pos,
      targets: Math.round(tgt),
      value: Math.round(pct * 10) / 10,
      lo: band[0], hi: band[1],
      basis: "player",
      flag,
    });
  }

  return out;
}

const GROUP_ENVELOPE_SUPPRESS_MIN = 80;

// WR carries sitting at the default 0% is the normal case (most WRs never
// get a carry share seeded), not a real undershoot worth flagging - a user
// who deliberately dials WR carries up to something like 12% still deserves
// the "high" flag if they overshoot, so only "low" is suppressed here.
const SUPPRESS_LOW_FOR = new Set(["carries|WR"]);

function sumAllocField(allocs, field, slotPrefix) {
  let sum = 0;
  for (const [key, a] of Object.entries(allocs || {})) {
    if (slotPrefix && !key.startsWith(slotPrefix)) continue;
    sum += a?.[field] ?? 0;
  }
  return sum;
}

// computeGroupEnvelopeFlags(allocs) -> [{ group, side, sharePct, lo, hi, flag }]
// One entry per (group, side) pair in GROUP_ENVELOPES (6 total), always
// returned - flag is "high"/"low"/null. Denominator is the team's OWN total
// allocated tgtPct/rushPct (not 100), so this stays meaningful for teams
// that haven't finished allocating yet. Flags for an entire side (targets
// or carries) are suppressed until that side's total allocated >= 80 -
// below that, a group's share is too noise-prone off a tiny denominator to
// be worth flagging.
export function computeGroupEnvelopeFlags(allocs) {
  const results = [];
  const totalTgt = sumAllocField(allocs, "tgtPct");
  const totalRush = sumAllocField(allocs, "rushPct");

  for (const [side, groups, total] of [
    ["targets", GROUP_ENVELOPES.targets, totalTgt],
    ["carries", GROUP_ENVELOPES.carries, totalRush],
  ]) {
    const field = side === "targets" ? "tgtPct" : "rushPct";
    for (const [group, [lo, hi]] of Object.entries(groups)) {
      const groupSum = sumAllocField(allocs, field, group);
      const sharePct = total > 0 ? Math.round((groupSum / total) * 1000) / 10 : 0;
      let flag = null;
      if (total >= GROUP_ENVELOPE_SUPPRESS_MIN) {
        if (sharePct > hi) flag = "high";
        else if (sharePct < lo && !SUPPRESS_LOW_FOR.has(`${side}|${group}`)) flag = "low";
      }
      results.push({ group, side, sharePct, lo, hi, flag });
    }
  }

  return results;
}
